import { cn } from '@plato/ui/util/classes';
import type { LucideProps } from 'lucide-react';
import * as React from 'react';
import Text, { type TextProps } from './text.tsx';

export type InfoTagProps = {
    icon?: React.ComponentType<LucideProps>;
    /**
     * Color for the text and icon
     * @default 'neutral-600'
     */
    color?: TextProps['color'];
    /**
     * Font weight for the text
     * @default 'normal'
     */
    weight?: TextProps['weight'];
    /**
     * Size for the text
     * @default 'sm'
     */
    size?: TextProps['size'];
} & React.HTMLAttributes<HTMLDivElement>;

/**
 * A component to display small informational text with an optional icon.
 * Used for metadata display in cards and lists.
 */
export const InfoTag = React.forwardRef<HTMLDivElement, InfoTagProps>(
    (
        { className, icon: Icon, children, color = 'neutral-600', weight = 'normal', size = 'sm', ...props },
        ref,
    ) => {
        // Don't render anything if there's no content
        if (!children) {
            return null;
        }

        return (
            <div
                className={cn('flex items-center overflow-hidden', className, {
                    'gap-1': size === 'xs',
                    'gap-2': size !== 'xs',
                })}
                ref={ref}
                {...props}
            >
                {Icon && <Icon className={cn('size-[1em] shrink-0', `text-${color}`)} />}
                <Text color={color} weight={weight} size={size} className="text-ellipsis overflow-hidden">
                    {children}
                </Text>
            </div>
        );
    },
);

InfoTag.displayName = 'InfoTag';
